import { getAuth } from 'firebase/auth';
import { serializeUserTraits } from './filterSerialization';
import { insightsErrorFromProblem, type InsightsProblemPayload } from './insightsErrors';
import type {
  CreativeDecisionPortfolioRequest,
  CreativeDecisionPortfolioResult,
  CreativeDecisionProjection,
  CreativeDecisionCopilotAnswer,
  CreativeDecisionCopilotAnswerRequest,
  CreativeLearningOperationsReport,
  ProviderSourceReportEnrichmentResult,
  ProviderSourceReportKey,
  CreativeReportEnrichmentResult,
} from '../creativeDecisions';
import {
  assertValidCreativeDecisionPortfolioResult,
  assertValidCreativeDecisionProjection,
  parseCreativeDecisionCopilotAnswer,
  parseCreativeLearningOperationsReport,
  parseProviderSourceReportEnrichmentResult,
  parseCreativeReportEnrichmentResult,
  CREATIVE_PROVIDER_SOURCE_REPORT_MAX_KEYS,
  CREATIVE_REPORT_ENRICHMENT_MAX_KEYS,
} from '../creativeDecisions';
import type {
  ExperimentFeasibilityPlan,
  ExperimentFeasibilityPlanData,
  FeasibilityPlanRequest,
  FunnelABTestDecisionReportData,
  FunnelABTestDecisionReportRequest,
  FunnelDropoffEdgeData,
  FunnelDropoffEdgeRequest,
  FunnelFilterOptions,
  FunnelInsightsResponse,
  FunnelOverlayData,
  FunnelOverlayRequest,
  FunnelReportsCoverageData,
  FunnelReportsCoverageRequest,
  FunnelReportsDashboardData,
  FunnelReportsSummaryData,
  FunnelReportsFunnelData,
  FunnelReportsAcquisitionData,
  FunnelReportsCreativesData,
  FunnelReportsDataHealthData,
  FunnelTrafficSourcesData,
  FunnelCreativeDrilldownData,
  FunnelReportsFunnelDirectoryData,
  FunnelReportsDashboardRequest,
  FunnelReportRefreshRequest,
  FunnelReportRefreshResult,
  FunnelScreenAnswersData,
  FunnelUserTraitsData,
  FunnelSegmentBreakdownData,
  JourneyCohortCensusData,
  JourneyCohortCensusRequest,
  WinnerRolloutExecuteResult,
  WinnerRolloutGate,
  WinnerRolloutPreviewData,
  WinnerRolloutRecord,
  WinnerRolloutRequest,
  WinnerRolloutRollbackRequest,
  WinnerRolloutVeto,
} from '../types';
import type { PricingReadinessData } from '../pricingServedPrefill';

function getBaseUrl(): string {
  if (import.meta.env.VITE_BUILDER_API_URL) {
    return import.meta.env.VITE_BUILDER_API_URL;
  }
  if (import.meta.env.VITE_AGENT_API_URL) {
    return import.meta.env.VITE_AGENT_API_URL;
  }

  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:8081';
  }
  if (hostname === 'dev.segmently.ai' || hostname.includes('devsegmentlyai')) {
    return 'https://dev-api.segmently.ai';
  }
  if (hostname === 'stage.segmently.ai' || hostname.includes('stagesegmentlyai')) {
    return 'https://stage-api.segmently.ai';
  }
  return 'https://api.segmently.ai';
}

async function getAuthHeaders(): Promise<Record<string, string>> {
  const token = await getAuth().currentUser?.getIdToken();
  if (!token) {
    throw new Error('User not authenticated');
  }
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

export { InsightsApiError } from './insightsErrors';

export interface CreativeProviderSourceSyncPreflight {
  preflightToken: string;
  scopeDigest: string;
  issuedAt: string;
}

export interface CreativeProviderSourceSyncProgress {
  syncRunId: string;
  taskId: string;
  status: 'pending' | 'running' | 'cancelling' | 'retrying' | 'needs_reauth'
    | 'paused_quota' | 'completed' | 'failed' | 'cancelled';
  outcome?: 'complete' | 'partial_success' | 'empty' | 'failed' | 'cancelled';
  attention?: { message: string };
}

export interface CreativeProviderSourceReportRowPreflightRequest {
  projectId: string;
  providerAccountId: string;
  providerAdId: string;
  expectedProviderCreativeId?: string;
  associationWindow: { from: string; to: string };
}

export interface CreativeProviderSourceSyncStartRequest {
  projectId: string;
  preflightToken: string;
  idempotencyKey: string;
}

export interface CreativeProviderSourceAgentResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  code?: string;
  status?: number;
}

async function handleAgentResponse<T>(response: Response): Promise<CreativeProviderSourceAgentResponse<T>> {
  const text = await response.text().catch(() => '');
  let payload: CreativeProviderSourceAgentResponse<T> | undefined;
  if (text) {
    try {
      payload = JSON.parse(text) as CreativeProviderSourceAgentResponse<T>;
    } catch {
      throw new Error(`Invalid JSON response from creative source API: HTTP ${response.status}`);
    }
  }
  if (!response.ok) {
    throw new Error(payload?.error || `Creative source API request failed: HTTP ${response.status}`);
  }
  if (!payload || typeof payload.success !== 'boolean') {
    throw new Error('Creative source API returned an invalid response envelope.');
  }
  return payload;
}

async function handleResponse<T>(response: Response): Promise<FunnelInsightsResponse<T>> {
  const text = await response.text().catch(() => '');
  let payload: FunnelInsightsResponse<T> | InsightsProblemPayload | undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new Error(`Invalid JSON response from insights API: HTTP ${response.status}`);
    }
  }

  if (!response.ok) {
    throw insightsErrorFromProblem(payload as InsightsProblemPayload | undefined, response.status);
  }

  return payload as FunnelInsightsResponse<T>;
}

// ── J2-I1 — winner rollout problem+json plumbing ─────────────────────────────

const WINNER_ROLLOUT_GATES: ReadonlySet<string> = new Set<WinnerRolloutGate>([
  'status', 'srm', 'data_quality', 'guardrail', 'sample_floor', 'identity',
]);

interface WinnerRolloutProblem {
  status: number;
  detail: string;
  errors: Array<{ field: string; message: string }>;
}

/**
 * J2-AC-EH2 — rollback published but the routing readback did not match the
 * stored snapshot. `servingState` carries the server's current-serving-state
 * readback (JSON string) so the UI is never a silent success.
 */
export class WinnerRolloutRollbackVerificationError extends Error {
  constructor(message: string, readonly servingState: string) {
    super(message);
    this.name = 'WinnerRolloutRollbackVerificationError';
  }
}

async function readWinnerRolloutProblem(response: Response): Promise<WinnerRolloutProblem> {
  const text = await response.text().catch(() => '');
  let payload: { detail?: string; title?: string; errors?: Array<{ field?: string; message?: string }> } | undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      payload = undefined;
    }
  }
  return {
    status: response.status,
    detail: payload?.detail || payload?.title || `HTTP ${response.status}`,
    errors: (payload?.errors ?? [])
      .filter((entry): entry is { field: string; message: string } =>
        typeof entry?.field === 'string' && typeof entry?.message === 'string'),
  };
}

function vetoesFromProblem(problem: WinnerRolloutProblem): WinnerRolloutVeto[] {
  return problem.errors
    .filter((entry) => WINNER_ROLLOUT_GATES.has(entry.field))
    .map((entry) => ({ gate: entry.field as WinnerRolloutGate, reason: entry.message }));
}

function buildWinnerRolloutBody(request: WinnerRolloutRequest): Record<string, unknown> {
  const body: Record<string, unknown> = {};
  if (request.winnerVariantId) body.winnerVariantId = request.winnerVariantId;
  if (request.scope) body.scope = request.scope;
  if (request.rationale) body.rationale = request.rationale;
  return body;
}

// ── pLTV package — report verdict-threshold settings plumbing ────────────────

/** Pricing-loop Phase 4 (AC-095/097): one durable dismissal per statement slug. */
export interface PricingDismissalEntry {
  reason: string;
  note?: string;
  target: 'hint' | 'candidate';
  at: string;
  by: string;
}

/** PATCH shape: slug → entry to store, or null to remove (restore). */
export type PricingDismissalsPatch = Record<
  string,
  { reason: string; note?: string; target: 'hint' | 'candidate' } | null
>;

export interface ReportVerdictSettingsData {
  verdictThresholds: import('../campaignVerdict').CampaignVerdictThresholds;
  isDefault: boolean;
  defaults: import('../campaignVerdict').CampaignVerdictThresholds;
  /** False for plain members — the popover renders read-only. */
  canEdit: boolean;
  /** Absent on pre-Phase-4 payloads. */
  pricingDismissals?: Record<string, PricingDismissalEntry>;
  updatedAt?: string;
  updatedBy?: string;
}

/** 400/403 from the settings endpoint with the server's named error code. */
export class ReportSettingsApiError extends Error {
  constructor(message: string, readonly status: number, readonly code?: string) {
    super(message);
    this.name = 'ReportSettingsApiError';
  }
}

async function handleReportSettingsResponse(response: Response): Promise<ReportVerdictSettingsData> {
  const text = await response.text().catch(() => '');
  let payload: { success?: boolean; data?: ReportVerdictSettingsData; error?: string; code?: string } | undefined;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      throw new ReportSettingsApiError(`Invalid JSON from report settings API: HTTP ${response.status}`, response.status);
    }
  }
  if (!response.ok || !payload?.data) {
    throw new ReportSettingsApiError(
      payload?.error ?? `HTTP ${response.status}`,
      response.status,
      payload?.code,
    );
  }
  return payload.data;
}

export class FunnelInsightsApiClient {
  constructor(private readonly baseUrl = getBaseUrl()) {}

  async getFunnelOverlay(request: FunnelOverlayRequest): Promise<FunnelInsightsResponse<FunnelOverlayData>> {
    const params = new URLSearchParams({
      onboardingId: request.onboardingId,
      versionId: request.versionId,
    });
    if (request.from) params.set('from', request.from);
    if (request.to) params.set('to', request.to);
    appendFilterParams(params, request.filters);

    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/funnel-overlay?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelOverlayData>(response);
  }

  /**
   * FU part F (D-F3): user answer / variable value distributions for ALL
   * screens of a version in ONE request (no per-screen fan-out).
   */
  async getScreenAnswers(request: {
    projectId: string;
    onboardingId: string;
    versionId: string;
    from?: string;
    to?: string;
    signal?: AbortSignal;
  }): Promise<FunnelInsightsResponse<FunnelScreenAnswersData>> {
    const params = new URLSearchParams({
      onboardingId: request.onboardingId,
      versionId: request.versionId,
    });
    if (request.from) params.set('from', request.from);
    if (request.to) params.set('to', request.to);

    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/screen-answers?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelScreenAnswersData>(response);
  }

  async getFilters(request: FunnelOverlayRequest): Promise<FunnelInsightsResponse<FunnelFilterOptions>> {
    const params = new URLSearchParams({
      onboardingId: request.onboardingId,
      versionId: request.versionId,
    });
    appendFilterParams(params, request.filters);

    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/filters?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelFilterOptions>(response);
  }

  async getReportsBreakdown(request: {
    projectId: string;
    dimension: string;
    onboardingId?: string;
    versionId?: string;
    from?: string;
    to?: string;
    abTestId?: string;
    signal?: AbortSignal;
  }): Promise<FunnelInsightsResponse<FunnelSegmentBreakdownData>> {
    const params = new URLSearchParams({ dimension: request.dimension });
    if (request.onboardingId) params.set('onboardingId', request.onboardingId);
    if (request.versionId) params.set('versionId', request.versionId);
    if (request.from) params.set('from', request.from);
    if (request.to) params.set('to', request.to);
    if (request.abTestId) params.set('abTestId', request.abTestId);

    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/breakdown?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelSegmentBreakdownData>(response);
  }

  /**
   * J1-I1 — Journey Cohort Census (Reports hub card). Composition-only read;
   * without a caller-provided `calibrationRunId` the model_cohorts family
   * arrives in its honest `no_calibration_artifacts` state — never send one
   * you did not already have.
   */
  async getJourneyCohortCensus(request: JourneyCohortCensusRequest): Promise<FunnelInsightsResponse<JourneyCohortCensusData>> {
    const params = new URLSearchParams();
    if (request.families?.length) params.set('families', request.families.join(','));
    if (request.onboardingId) params.set('onboardingId', request.onboardingId);
    if (request.versionId) params.set('versionId', request.versionId);
    if (request.from) params.set('from', request.from);
    if (request.to) params.set('to', request.to);
    if (request.calibrationRunId) params.set('calibrationRunId', request.calibrationRunId);
    if (request.outcome) params.set('outcome', request.outcome);

    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/journey-cohort-census?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<JourneyCohortCensusData>(response);
  }

  async getUserTraits(request: Pick<FunnelOverlayRequest, 'projectId' | 'signal'> & Partial<Pick<FunnelOverlayRequest, 'onboardingId' | 'versionId' | 'from' | 'to'>>): Promise<FunnelInsightsResponse<FunnelUserTraitsData>> {
    const params = new URLSearchParams();
    if (request.onboardingId) params.set('onboardingId', request.onboardingId);
    if (request.versionId) params.set('versionId', request.versionId);
    if (request.from) params.set('from', request.from);
    if (request.to) params.set('to', request.to);

    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/user-traits?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelUserTraitsData>(response);
  }

  async getDropoffEdge(request: FunnelDropoffEdgeRequest): Promise<FunnelInsightsResponse<FunnelDropoffEdgeData>> {
    const params = new URLSearchParams({
      onboardingId: request.onboardingId,
      versionId: request.versionId,
    });
    if (request.canvasTransitionId) params.set('canvasTransitionId', request.canvasTransitionId);
    // I-A stub-click mode — leaver audience instead of edge segments.
    if (request.dropScreenId) params.set('dropScreenId', request.dropScreenId);
    if (request.from) params.set('from', request.from);
    if (request.to) params.set('to', request.to);
    appendFilterParams(params, request.filters);

    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/dropoff-edge?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelDropoffEdgeData>(response);
  }

  async refreshReports(request: FunnelReportRefreshRequest): Promise<FunnelInsightsResponse<FunnelReportRefreshResult>> {
    const body = {
      onboardingId: request.onboardingId,
      versionId: request.versionId,
      from: request.from,
      to: request.to,
      target: request.target ?? 'reports',
      dryRun: request.dryRun === true,
    };

    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/refresh`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(body),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelReportRefreshResult>(response);
  }

  /**
   * PE-6a: which report periods are ready for the scope. Callers must treat
   * any failure as «no coverage» and degrade to envelope anchoring — this
   * request never blocks the dashboard.
   */
  async getReportsCoverage(request: FunnelReportsCoverageRequest): Promise<FunnelInsightsResponse<FunnelReportsCoverageData>> {
    const params = new URLSearchParams();
    if (request.onboardingId) params.set('onboardingId', request.onboardingId);
    if (request.versionId) params.set('versionId', request.versionId);
    if (request.alias) params.set('alias', request.alias);

    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/coverage?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelReportsCoverageData>(response);
  }

  /**
   * pLTV package — campaign verdict thresholds (projects/{pid}/settings/
   * reports). Plain {success, data} envelope, not the insights freshness
   * envelope. PATCH is owner/admin-only (403 for plain members); validation
   * failures answer 400 with a named `code` — surfaced verbatim so the
   * popover can map it to inline copy.
   */
  async getReportSettings(request: { projectId: string; signal?: AbortSignal }): Promise<ReportVerdictSettingsData> {
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/settings`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleReportSettingsResponse(response);
  }

  async patchReportSettings(request: {
    projectId: string;
    patch: Partial<import('../campaignVerdict').CampaignVerdictThresholds>
      & { pricingDismissals?: PricingDismissalsPatch };
    signal?: AbortSignal;
  }): Promise<ReportVerdictSettingsData> {
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/settings`,
      {
        method: 'PATCH',
        headers: await getAuthHeaders(),
        body: JSON.stringify(request.patch),
        signal: request.signal,
      },
    );
    return handleReportSettingsResponse(response);
  }

  async getReportsDashboard(request: FunnelReportsDashboardRequest): Promise<FunnelInsightsResponse<FunnelReportsDashboardData>> {
    const params = buildReportsDashboardParams(request);
    if (request.limit) params.set('limit', String(request.limit));

    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/dashboard?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelReportsDashboardData>(response);
  }

  /**
   * W-R4-2 (DISC-AC-133/142b): the split summary endpoint — header KPIs +
   * prior comparison at summary cost. The AC-142b prior UPGRADE path: when
   * coverage settles after an un-gated URL-explicit first paint, this refetch
   * fills priorSummary without re-running the monolith.
   */
  async getReportsSummary(request: FunnelReportsDashboardRequest): Promise<FunnelInsightsResponse<FunnelReportsSummaryData>> {
    const params = buildReportsDashboardParams(request);
    if (request.limit) params.set('limit', String(request.limit));
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/summary?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelReportsSummaryData>(response);
  }

  /**
   * W-R5 (DISC-AC-133/143): the remaining split endpoints — the page fetches
   * its five section groups in parallel instead of the monolith. Each method
   * shares the SAME param builder as the monolith (parity of inputs).
   */
  async getReportsFunnelSection(request: FunnelReportsDashboardRequest): Promise<FunnelInsightsResponse<FunnelReportsFunnelData>> {
    const params = buildReportsDashboardParams(request);
    if (request.limit) params.set('limit', String(request.limit));
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/funnel?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelReportsFunnelData>(response);
  }

  async getReportsAcquisition(request: FunnelReportsDashboardRequest): Promise<FunnelInsightsResponse<FunnelReportsAcquisitionData>> {
    const params = buildReportsDashboardParams(request);
    params.set('entityType', 'campaign');
    // F3 (FT-AC-089): explicit model selection rides ONLY the acquisition
    // lane; absent = the served default (request bytes unchanged pre-F3).
    if (request.attributionModelId) params.set('attributionModelId', request.attributionModelId);
    if (request.limit) params.set('limit', String(request.limit));
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/acquisition?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelReportsAcquisitionData>(response);
  }

  /**
   * People-attribution S10 (D-K/D-M): «Traffic into this funnel» — the ONE
   * served method behind the version-row «Sources» popup and its route.
   * `unit` drives the server-side spend share (people | sessions).
   */
  async getReportsFunnelTrafficSources(
    request: FunnelReportsDashboardRequest & { unit: 'people' | 'visits' },
  ): Promise<FunnelInsightsResponse<FunnelTrafficSourcesData>> {
    const params = buildReportsDashboardParams(request);
    params.set('unit', request.unit);
    if (request.attributionModelId) params.set('attributionModelId', request.attributionModelId);
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/funnel-traffic-sources?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelTrafficSourcesData>(response);
  }

  async getReportsCreatives(
    request: FunnelReportsDashboardRequest & { view?: 'performance' | 'diagnostics' },
  ): Promise<FunnelInsightsResponse<FunnelReportsCreativesData>> {
    const params = buildReportsDashboardParams(request);
    if (request.view) params.set('view', request.view);
    if (request.limit) params.set('limit', String(request.limit));
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/creatives?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelReportsCreativesData>(response);
  }

  async getReportsDataHealth(request: FunnelReportsDashboardRequest): Promise<FunnelInsightsResponse<FunnelReportsDataHealthData>> {
    const params = buildReportsDashboardParams(request);
    if (request.limit) params.set('limit', String(request.limit));
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/data-health?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelReportsDataHealthData>(response);
  }

  /**
   * W-R4-2 (DISC-AC-134): lazy per-campaign creative drilldown page. The
   * cursor is a server-minted fingerprint token — a continuation under
   * changed filters is refused server-side (CURSOR-KEEP), so callers restart
   * from the first page whenever the request identity changes.
   */
  async getReportsCreativesDrilldown(
    request: FunnelReportsDashboardRequest & { campaignId: string; cursor?: string },
  ): Promise<FunnelInsightsResponse<FunnelCreativeDrilldownData>> {
    const params = buildReportsDashboardParams(request);
    params.set('view', 'performance');
    params.set('campaignId', request.campaignId);
    if (request.limit) params.set('limit', String(request.limit));
    if (request.cursor) params.set('cursor', request.cursor);
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/creatives?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelCreativeDrilldownData>(response);
  }

  /**
   * W-R4-2 (DISC-AC-154): funnel-first directory — one row per funnel with
   * traffic in [from, to], identity-enriched (names + linked placements).
   */
  async getReportsFunnels(
    request: {
      projectId: string;
      from: string;
      to: string;
      onboardingId?: string;
      versionId?: string;
      series?: boolean;
      limit?: number;
      signal?: AbortSignal;
    },
  ): Promise<FunnelInsightsResponse<FunnelReportsFunnelDirectoryData>> {
    const params = new URLSearchParams({ from: request.from, to: request.to });
    if (request.onboardingId) params.set('onboardingId', request.onboardingId);
    if (request.versionId) params.set('versionId', request.versionId);
    if (request.series) params.set('series', 'daily');
    if (request.limit) params.set('limit', String(request.limit));
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/funnels?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelReportsFunnelDirectoryData>(response);
  }

  /**
   * Pricing-loop v2 Phase 1 (AC-013/014): per-lane readiness + served prefill
   * for the hub calculator. `prefill: true` also runs the spend lane (heavier
   * — the «pull served» click, not the tab open).
   */
  async getPricingReadiness(
    request: {
      projectId: string;
      onboardingId?: string;
      versionId?: string;
      from: string;
      to: string;
      prefill?: boolean;
      signal?: AbortSignal;
    },
  ): Promise<FunnelInsightsResponse<PricingReadinessData>> {
    const params = new URLSearchParams({ from: request.from, to: request.to });
    if (request.onboardingId) params.set('onboardingId', request.onboardingId);
    if (request.versionId) params.set('versionId', request.versionId);
    if (request.prefill) params.set('prefill', '1');
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/pricing/readiness?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<PricingReadinessData>(response);
  }

  async getCreativeDecisions(
    request: CreativeDecisionPortfolioRequest & { signal?: AbortSignal },
  ): Promise<FunnelInsightsResponse<CreativeDecisionPortfolioResult>> {
    const response = await fetch(
      buildCreativeDecisionsPortfolioUrl(this.baseUrl, request),
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    const envelope = await handleResponse<CreativeDecisionPortfolioResult>(response);
    assertValidCreativeDecisionPortfolioResult(envelope.data);
    return envelope;
  }

  async getCreativeDecisionEnrichment(request: {
    projectId: string;
    deploymentKeys: readonly string[];
    signal?: AbortSignal;
  }): Promise<FunnelInsightsResponse<CreativeReportEnrichmentResult>> {
    const prepared = buildCreativeDecisionEnrichmentRequest(this.baseUrl, request);
    const response = await fetch(prepared.url, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: prepared.body,
      signal: request.signal,
    });
    const envelope = await handleResponse<CreativeReportEnrichmentResult>(response);
    const parsed = parseCreativeReportEnrichmentResult(envelope.data);
    const returned = new Set(parsed.items.map((item) => item.deploymentKey));
    if (returned.size !== prepared.deploymentKeys.length
      || prepared.deploymentKeys.some((deploymentKey) => !returned.has(deploymentKey))) {
      throw new Error('Creative report enrichment response does not match the requested deployment keys.');
    }
    return { ...envelope, data: parsed };
  }

  async getCreativeProviderSourceEnrichment(request: {
    projectId: string;
    items: readonly ProviderSourceReportKey[];
    signal?: AbortSignal;
  }): Promise<FunnelInsightsResponse<ProviderSourceReportEnrichmentResult>> {
    const prepared = buildCreativeProviderSourceEnrichmentRequest(this.baseUrl, request);
    const response = await fetch(prepared.url, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: prepared.body,
      signal: request.signal,
    });
    const envelope = await handleResponse<ProviderSourceReportEnrichmentResult>(response);
    const parsed = parseProviderSourceReportEnrichmentResult(envelope.data);
    const returned = new Map(parsed.items.map((item) => [item.correlationKey, item]));
    if (returned.size !== prepared.items.length || prepared.items.some((requested) => {
      const item = returned.get(requested.correlationKey);
      return !item
        || item.provider !== requested.provider
        || item.providerAccountId !== requested.providerAccountId
        || item.providerAdId !== requested.providerAdId
        || (requested.providerCreativeId !== undefined
          && item.providerCreativeId !== requested.providerCreativeId);
    })) {
      throw new Error('Provider source report enrichment response does not match the requested provider keys.');
    }
    return { ...envelope, data: parsed };
  }

  async preflightProviderCreativeSourceReportRow(request: {
    projectId: string;
    providerAccountId: string;
    providerAdId: string;
    expectedProviderCreativeId?: string;
    associationWindow: { from: string; to: string };
    signal?: AbortSignal;
  }): Promise<CreativeProviderSourceAgentResponse<{ preflight: CreativeProviderSourceSyncPreflight }>> {
    const prepared = buildCreativeProviderSourceReportRowPreflightRequest(this.baseUrl, request);
    const response = await fetch(prepared.url, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: prepared.body,
      signal: request.signal,
    });
    return handleAgentResponse<{ preflight: CreativeProviderSourceSyncPreflight }>(response);
  }

  async syncMetaCreativeSources(request: {
    projectId: string;
    preflightToken: string;
    idempotencyKey: string;
    signal?: AbortSignal;
  }): Promise<CreativeProviderSourceAgentResponse<{
    syncRunId: string;
    taskId: string;
    status: CreativeProviderSourceSyncProgress['status'];
  }>> {
    const prepared = buildCreativeProviderSourceSyncStartRequest(this.baseUrl, request);
    const response = await fetch(prepared.url, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: prepared.body,
      signal: request.signal,
    });
    return handleAgentResponse(response);
  }

  async getProviderCreativeSourceSync(
    projectIdInput: string,
    syncRunIdInput: string,
    signal?: AbortSignal,
  ): Promise<CreativeProviderSourceAgentResponse<CreativeProviderSourceSyncProgress>> {
    const response = await fetch(
      buildCreativeProviderSourceSyncProgressUrl(this.baseUrl, projectIdInput, syncRunIdInput),
      { headers: await getAuthHeaders(), signal },
    );
    return handleAgentResponse(response);
  }

  /**
   * Manual half of the cached-first hybrid (topup plan S1): asks the server
   * to recompute + persist the canonical default snapshot. Returns a small
   * summary — the caller re-GETs the portfolio to render fresh data.
   */
  async refreshCreativeDecisionsPortfolioSnapshot(
    request: { projectId: string; signal?: AbortSignal },
  ): Promise<FunnelInsightsResponse<{ computedAt: string; rows: number }>> {
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/creative-decisions/portfolio-snapshot/refresh`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<{ computedAt: string; rows: number }>(response);
  }

  /**
   * I5b (unlock-prod-money-values) — money-policy banner state-2 CTA: re-sync
   * Meta delivery for ONE account over the portfolio window so unbound spend
   * rows re-bind to the ACTIVE registered money policy. No policy version is
   * passed on purpose — the server's registry auto-resolve picks the active
   * revision (the I0 binding fix). Response is a plain sync summary, not the
   * insights envelope; callers only need success/failure.
   */
  async resyncMetaAdsDelivery(request: {
    projectId: string;
    adAccountId: string;
    /** YYYY-MM-DD inclusive window bounds, matching the sync-delivery contract. */
    since: string;
    until: string;
    signal?: AbortSignal;
  }): Promise<void> {
    const response = await fetch(`${this.baseUrl}/api/meta-ads/sync-delivery`, {
      method: 'POST',
      headers: await getAuthHeaders(),
      body: JSON.stringify({
        projectId: request.projectId,
        adAccountId: request.adAccountId,
        since: request.since,
        until: request.until,
      }),
      signal: request.signal,
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let detail = `HTTP ${response.status}`;
      try {
        const payload = JSON.parse(text) as { error?: string };
        if (payload.error) detail = payload.error;
      } catch { /* plain-text or empty body — keep the status detail */ }
      throw new Error(`Meta delivery re-sync failed: ${detail}`);
    }
  }

  async getCreativeDecision(
    request: Pick<CreativeDecisionPortfolioRequest, 'projectId' | 'from' | 'to'> & {
      deploymentKey: string;
      signal?: AbortSignal;
    },
  ): Promise<FunnelInsightsResponse<CreativeDecisionProjection>> {
    const params = new URLSearchParams({ from: request.from, to: request.to });
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/creative-decisions/${encodeURIComponent(request.deploymentKey)}?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    const envelope = await handleResponse<CreativeDecisionProjection>(response);
    assertValidCreativeDecisionProjection(envelope.data);
    return envelope;
  }

  async answerCreativeDecisionCopilot(
    request: CreativeDecisionCopilotAnswerRequest & { projectId: string },
  ): Promise<CreativeDecisionCopilotAnswer> {
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/creative-decisions/ops/copilot/answers`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          snapshotId: request.snapshotId,
          projectionHash: request.projectionHash,
          question: request.question,
        }),
        signal: request.signal,
      },
    );
    const envelope = await handleResponse<CreativeDecisionCopilotAnswer>(response);
    return parseCreativeDecisionCopilotAnswer(envelope.data, {
      projectId: request.projectId,
      snapshotId: request.snapshotId,
      projectionHash: request.projectionHash,
    });
  }

  async getCreativeLearningOperationsReport(request: {
    projectId: string;
    from: string;
    to: string;
    signal?: AbortSignal;
  }): Promise<FunnelInsightsResponse<CreativeLearningOperationsReport>> {
    const response = await fetch(
      buildCreativeLearningOperationsReportUrl(this.baseUrl, request),
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    const envelope = await handleResponse<CreativeLearningOperationsReport>(response);
    const report = parseCreativeLearningOperationsReport(envelope.data);
    if (report.projectId !== request.projectId
      || report.window.from !== new Date(request.from).toISOString()
      || report.window.to !== new Date(request.to).toISOString()) {
      throw new Error('Learning report scope does not match the requested project and window.');
    }
    return { ...envelope, data: report };
  }

  /**
   * J1 — advisory experiment MDE/feasibility plan for the hypothesis form.
   * A failure here is advisory-only: callers render a retriable state and the
   * form stays submittable.
   */
  async planExperimentMde(request: FeasibilityPlanRequest): Promise<ExperimentFeasibilityPlan> {
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/planner/mde`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({
          scope: request.scope,
          overrides: request.overrides,
          ...(request.windowDays !== undefined && { windowDays: request.windowDays }),
        }),
        signal: request.signal,
      },
    );
    const payload = await handleResponse<ExperimentFeasibilityPlanData>(response);
    return payload.data.plan;
  }

  async getABTestDecisionReport(request: FunnelABTestDecisionReportRequest): Promise<FunnelInsightsResponse<FunnelABTestDecisionReportData>> {
    const params = buildReportsDashboardParams(request);
    if (request.abTestId) params.set('abTestId', request.abTestId);
    if (request.abTestIterationId) params.set('abTestIterationId', request.abTestIterationId);
    if (request.controlVariantId) params.set('controlVariantId', request.controlVariantId);
    if (request.primaryMetric) params.set('primaryMetric', request.primaryMetric);
    if (request.limit) params.set('limit', String(request.limit));

    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/ab-test-decisions?${params.toString()}`,
      {
        headers: await getAuthHeaders(),
        signal: request.signal,
      },
    );
    return handleResponse<FunnelABTestDecisionReportData>(response);
  }

  /**
   * J2-I1 — dry preview for the winner-rollout dialog: decision digest, the
   * server-evaluated gate vetoes, current weights and the planned chain.
   * Read-only; nothing is mutated by a preview.
   */
  async previewWinnerRollout(request: WinnerRolloutRequest): Promise<WinnerRolloutPreviewData> {
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/experiments/${encodeURIComponent(request.experimentId)}/rollout/preview`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(buildWinnerRolloutBody(request)),
        signal: request.signal,
      },
    );
    const payload = await handleResponse<WinnerRolloutPreviewData>(response);
    return payload.data;
  }

  /**
   * J2-I1 — execute the rollout. A 422 problem+json response is the
   * server-side veto contract (errors[].field = exact failing gate) and is
   * returned as a typed `{ kind: 'vetoed' }` result, never thrown — the
   * dialog re-renders the veto state from it.
   */
  async executeWinnerRollout(request: WinnerRolloutRequest): Promise<WinnerRolloutExecuteResult> {
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/experiments/${encodeURIComponent(request.experimentId)}/rollout`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify(buildWinnerRolloutBody(request)),
        signal: request.signal,
      },
    );
    if (response.status === 422) {
      const problem = await readWinnerRolloutProblem(response);
      const vetoes = vetoesFromProblem(problem);
      if (vetoes.length > 0) {
        return { kind: 'vetoed', vetoes };
      }
      throw new Error(problem.detail);
    }
    const payload = await handleResponse<{ rollout: WinnerRolloutRecord }>(response);
    return { kind: 'completed', rollout: payload.data.rollout };
  }

  /**
   * J2-I1 — one-click rollback of a completed rollout. A verification failure
   * (HTTP 500, errors[0].field === 'servingState') throws
   * `WinnerRolloutRollbackVerificationError` carrying the server's
   * current-serving-state readback — never a silent success.
   */
  async rollbackWinnerRollout(request: WinnerRolloutRollbackRequest): Promise<WinnerRolloutRecord> {
    const response = await fetch(
      `${this.baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/experiments/${encodeURIComponent(request.experimentId)}/rollout/rollback`,
      {
        method: 'POST',
        headers: await getAuthHeaders(),
        body: JSON.stringify({ rolloutId: request.rolloutId }),
        signal: request.signal,
      },
    );
    if (!response.ok) {
      const problem = await readWinnerRolloutProblem(response);
      const servingState = problem.errors.find((entry) => entry.field === 'servingState');
      if (servingState) {
        throw new WinnerRolloutRollbackVerificationError(problem.detail, servingState.message);
      }
      throw new Error(problem.detail);
    }
    const payload = await handleResponse<{ rollout: WinnerRolloutRecord }>(response);
    return payload.data.rollout;
  }
}

export function buildCreativeLearningOperationsReportUrl(
  baseUrl: string,
  request: Pick<CreativeDecisionPortfolioRequest, 'projectId' | 'from' | 'to'>,
): string {
  const params = new URLSearchParams({ from: request.from, to: request.to });
  return `${baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/creative-decisions/ops/learning-report?${params.toString()}`;
}

export function buildCreativeDecisionEnrichmentRequest(
  baseUrl: string,
  request: { projectId: string; deploymentKeys: readonly string[] },
): { url: string; body: string; deploymentKeys: string[] } {
  const projectId = request.projectId.trim();
  if (!projectId) throw new Error('projectId is required for creative report enrichment.');
  if (!Array.isArray(request.deploymentKeys)) throw new Error('deploymentKeys must be an array.');
  const deploymentKeys = Array.from(new Set(request.deploymentKeys.map((value) => String(value).trim())));
  if (deploymentKeys.some((value) => !value)) throw new Error('deploymentKeys must not contain empty values.');
  if (deploymentKeys.length === 0 || deploymentKeys.length > CREATIVE_REPORT_ENRICHMENT_MAX_KEYS) {
    throw new Error(`Creative report enrichment accepts between 1 and ${CREATIVE_REPORT_ENRICHMENT_MAX_KEYS} deployment keys.`);
  }
  return {
    url: `${baseUrl}/api/projects/${encodeURIComponent(projectId)}/insights/v1/reports/creative-decisions/enrichment`,
    body: JSON.stringify({ deploymentKeys }),
    deploymentKeys,
  };
}

export function buildCreativeProviderSourceEnrichmentRequest(
  baseUrl: string,
  request: { projectId: string; items: readonly ProviderSourceReportKey[] },
): { url: string; body: string; items: ProviderSourceReportKey[] } {
  const projectId = boundedRequestString(request.projectId, 'projectId', 200);
  if (projectId === '.' || projectId === '..' || projectId.includes('/')) {
    throw new Error('projectId must be a Firestore document id for provider source report enrichment.');
  }
  if (!Array.isArray(request.items)
    || request.items.length === 0
    || request.items.length > CREATIVE_PROVIDER_SOURCE_REPORT_MAX_KEYS) {
    throw new Error(
      `Provider source report enrichment accepts between 1 and ${CREATIVE_PROVIDER_SOURCE_REPORT_MAX_KEYS} keys.`,
    );
  }
  const seen = new Set<string>();
  const items = request.items.map((value, index): ProviderSourceReportKey => {
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw new Error(`items[${index}] must be an object.`);
    }
    const unsupported = Object.keys(value).filter((key) => ![
      'correlationKey',
      'provider',
      'providerAccountId',
      'providerAdId',
      'providerCreativeId',
    ].includes(key));
    if (unsupported.length > 0) {
      throw new Error(`items[${index}] contains unsupported fields: ${unsupported.join(', ')}.`);
    }
    const correlationKey = boundedRequestString(value.correlationKey, `items[${index}].correlationKey`, 200);
    if (seen.has(correlationKey)) {
      throw new Error(`Duplicate provider source report correlation key: ${correlationKey}.`);
    }
    seen.add(correlationKey);
    if (value.provider !== 'meta') throw new Error(`items[${index}].provider must be meta.`);
    const accountRaw = boundedRequestString(
      value.providerAccountId,
      `items[${index}].providerAccountId`,
      128,
    ).replace(/^act_/, '');
    if (!/^\d+$/.test(accountRaw)) {
      throw new Error(`items[${index}].providerAccountId must be a numeric Meta account id.`);
    }
    const providerAdId = optionalRequestMetaId(value.providerAdId, `items[${index}].providerAdId`);
    const providerCreativeId = optionalRequestMetaId(
      value.providerCreativeId,
      `items[${index}].providerCreativeId`,
    );
    if (!providerAdId && !providerCreativeId) {
      throw new Error(`items[${index}] requires providerAdId or providerCreativeId.`);
    }
    return compactRequest({
      correlationKey,
      provider: 'meta' as const,
      providerAccountId: `act_${accountRaw}`,
      providerAdId,
      providerCreativeId,
    });
  });
  return {
    url: `${baseUrl}/api/projects/${encodeURIComponent(projectId)}/insights/v1/reports/creative-decisions/source-enrichment`,
    body: JSON.stringify({ items }),
    items,
  };
}

export function buildCreativeProviderSourceReportRowPreflightRequest(
  baseUrl: string,
  request: CreativeProviderSourceReportRowPreflightRequest,
): { url: string; body: string } {
  const projectId = requestProjectDocumentId(request.projectId);
  const providerAccountId = normalizedRequestMetaAccountId(request.providerAccountId, 'providerAccountId');
  const providerAdId = requiredRequestMetaId(request.providerAdId, 'providerAdId');
  const expectedProviderCreativeId = optionalRequestMetaId(
    request.expectedProviderCreativeId,
    'expectedProviderCreativeId',
  );
  const associationWindow = normalizedRequestAssociationWindow(request.associationWindow);
  return {
    url: `${baseUrl}/api/agent/ad-creative/source-syncs/report-row-preflight`,
    body: JSON.stringify(compactRequest({
      projectId,
      providerAccountId,
      providerAdId,
      expectedProviderCreativeId,
      associationWindow,
    })),
  };
}

export function buildCreativeProviderSourceSyncStartRequest(
  baseUrl: string,
  request: CreativeProviderSourceSyncStartRequest,
): { url: string; body: string } {
  return {
    url: `${baseUrl}/api/agent/ad-creative/source-syncs`,
    body: JSON.stringify({
      projectId: requestProjectDocumentId(request.projectId),
      preflightToken: boundedRequestString(request.preflightToken, 'preflightToken', 256),
      idempotencyKey: boundedRequestString(request.idempotencyKey, 'idempotencyKey', 512),
    }),
  };
}

export function buildCreativeProviderSourceSyncProgressUrl(
  baseUrl: string,
  projectIdInput: string,
  syncRunIdInput: string,
): string {
  const projectId = requestProjectDocumentId(projectIdInput);
  const syncRunId = boundedRequestString(syncRunIdInput, 'syncRunId', 256);
  return `${baseUrl}/api/agent/ad-creative/source-syncs/${encodeURIComponent(syncRunId)}?${new URLSearchParams({ projectId }).toString()}`;
}

function requestProjectDocumentId(value: unknown): string {
  const projectId = boundedRequestString(value, 'projectId', 200);
  if (projectId === '.' || projectId === '..' || projectId.includes('/')) {
    throw new Error('projectId must be a Firestore document id.');
  }
  return projectId;
}

function normalizedRequestAssociationWindow(
  value: CreativeProviderSourceReportRowPreflightRequest['associationWindow'],
): { from: string; to: string } {
  const from = requestIsoDate(value?.from, 'associationWindow.from');
  const to = requestIsoDate(value?.to, 'associationWindow.to');
  const days = (Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`)) / 86_400_000;
  if (days < 1 || days > 366) {
    throw new Error('associationWindow must cover 1 to 366 days with an exclusive to date.');
  }
  return { from, to };
}

function requestIsoDate(value: unknown, field: string): string {
  const text = boundedRequestString(value, field, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/u.test(text) || Number.isNaN(Date.parse(`${text}T00:00:00.000Z`))) {
    throw new Error(`${field} must be an ISO date.`);
  }
  return text;
}

function boundedRequestString(value: unknown, field: string, maxLength: number): string {
  const result = typeof value === 'string' ? value.trim() : '';
  if (!result || result.length > maxLength || /[\u0000-\u001f\u007f]/.test(result)) {
    throw new Error(`${field} must be a bounded non-empty string.`);
  }
  return result;
}

function optionalRequestMetaId(value: unknown, field: string): string | undefined {
  if (value === undefined) return undefined;
  const result = boundedRequestString(value, field, 64);
  if (!/^\d{1,64}$/u.test(result)) throw new Error(`${field} must be a numeric Meta id.`);
  return result;
}

function requiredRequestMetaId(value: unknown, field: string): string {
  const result = optionalRequestMetaId(value, field);
  if (!result) throw new Error(`${field} must be a numeric Meta id.`);
  return result;
}

function normalizedRequestMetaAccountId(value: unknown, field: string): string {
  const raw = boundedRequestString(value, field, 68).replace(/^act_/, '');
  if (!/^\d{1,64}$/u.test(raw)) throw new Error(`${field} must be a numeric Meta account id.`);
  return `act_${raw}`;
}

function compactRequest<T extends Record<string, unknown>>(value: T): T {
  return Object.fromEntries(Object.entries(value).filter(([, entry]) => entry !== undefined)) as T;
}

function buildReportsDashboardParams(request: FunnelReportsDashboardRequest): URLSearchParams {
  const params = new URLSearchParams({
    from: request.from,
    to: request.to,
  });
  if (request.onboardingId) params.set('onboardingId', request.onboardingId);
  if (request.versionId) params.set('versionId', request.versionId);
  if (request.alias) params.set('alias', request.alias);
  if (request.paidFilters?.campaign) params.set('paidCampaign', request.paidFilters.campaign);
  if (request.paidFilters?.metaAdAccountId) params.set('metaAdAccountId', request.paidFilters.metaAdAccountId);
  if (request.paidFilters?.metaCampaignId) params.set('metaCampaignId', request.paidFilters.metaCampaignId);
  if (request.paidFilters?.metaAdsetId) params.set('metaAdsetId', request.paidFilters.metaAdsetId);
  if (request.paidFilters?.metaAdId) params.set('metaAdId', request.paidFilters.metaAdId);
  if (request.paidFilters?.metaCreativeId) params.set('metaCreativeId', request.paidFilters.metaCreativeId);
  if (request.paidFilters?.matchQuality) params.set('matchQuality', request.paidFilters.matchQuality);
  if (request.compare) params.set('compare', request.compare);
  if (request.tz) params.set('tz', request.tz);
  return params;
}

/** Deterministic list URL shared by the browser client and contract coverage. */
export function buildCreativeDecisionsPortfolioUrl(
  baseUrl: string,
  request: CreativeDecisionPortfolioRequest,
): string {
  const params = new URLSearchParams({
    from: request.from,
    to: request.to,
  });
  appendListParam(params, 'provider', request.providers);
  appendListParam(params, 'providerAccountId', request.providerAccountIds);
  appendListParam(params, 'lifecycleStage', request.lifecycleStages);
  appendListParam(params, 'dataState', request.dataStates);
  appendListParam(params, 'decision', request.decisions);
  appendListParam(params, 'evidenceClass', request.evidenceClasses);
  appendListParam(params, 'origin', request.origins);
  appendListParam(params, 'campaignFamily', request.campaignFamilies);
  appendListParam(params, 'network', request.networks);
  appendListParam(params, 'funnel', request.funnels);
  appendListParam(params, 'geo', request.geos);
  appendListParam(params, 'purchaseMode', request.purchaseModes);
  appendListParam(params, 'subscriptionEntry', request.subscriptionEntries);
  appendListParam(params, 'productId', request.productIds);
  appendListParam(params, 'planId', request.planIds);
  appendListParam(params, 'priceId', request.priceIds);
  appendListParam(params, 'originalCurrency', request.originalCurrencies);
  appendListParam(params, 'reportingCurrency', request.reportingCurrencies);
  appendListParam(params, 'maturity', request.maturityStates);
  appendListParam(params, 'currency', request.currencies);
  if (request.deploymentScope) params.set('deploymentScope', request.deploymentScope);
  if (request.sort) params.set('sort', request.sort);
  if (request.limit !== undefined) params.set('limit', String(request.limit));
  if (request.cursor) params.set('cursor', request.cursor);
  return `${baseUrl}/api/projects/${encodeURIComponent(request.projectId)}/insights/v1/reports/creative-decisions?${params.toString()}`;
}

function appendListParam(
  params: URLSearchParams,
  key: string,
  values: readonly string[] | undefined,
): void {
  if (values?.length) params.set(key, values.join(','));
}

function appendFilterParams(params: URLSearchParams, filters: FunnelOverlayRequest['filters']): void {
  if (!filters) return;
  if (filters.country) params.set('country', filters.country);
  if (filters.device) params.set('device', filters.device);
  if (filters.variant) params.set('variant', filters.variant);
  if (filters.attributionStatus) params.set('attributionStatus', filters.attributionStatus);
  if (filters.userTraitKey && filters.userTraitValue) {
    params.set('userTraitKey', filters.userTraitKey);
    params.set('userTraitValue', filters.userTraitValue);
  }
  const userTraits = serializeUserTraits(filters.userTraits);
  if (userTraits) {
    params.set('userTraits', userTraits);
  }
  // I-B2 «is not» clauses — same JSON param shape as userTraits (P-5).
  const userTraitsExclude = serializeUserTraits(filters.userTraitsExclude);
  if (userTraitsExclude) {
    params.set('userTraitsExclude', userTraitsExclude);
  }
}
